/**
 * Sanki — hero-atelier video background.
 *
 * Drop-in replacement for Atelier's `video-background-component` without
 * ES-module imports. Pattern:
 *   • <sk-video-background> wraps a <video> + one or more <source> tags.
 *   • Sources use `data-video-source="..."` instead of `src="..."` so the
 *     browser doesn't start downloading until this element is upgraded.
 *   • On connect, we copy data-video-source → src, then call .load().
 *   • Honours prefers-reduced-motion: if set, we leave the poster image
 *     in place and never trigger autoplay.
 *   • Pauses autoplay when the section is scrolled out of view (saves
 *     bandwidth on long pages).
 */
(function () {
  'use strict';

  if (customElements.get('sk-video-background')) return;

  class SkVideoBackground extends HTMLElement {
    connectedCallback() {
      // Respect the user's reduced-motion preference.
      const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      const video   = this.querySelector('[data-sk-video-element]');
      const sources = this.querySelectorAll('[data-sk-video-source]');

      if (!video || !sources.length) return;

      // Promote data-video-source -> src. Doing it here (not in the HTML)
      // defers the network request until the element is in the DOM, which
      // keeps the LCP image / text rendering fast.
      for (const src of sources) {
        const url = src.dataset.videoSource;
        if (url) src.setAttribute('src', url);
      }
      video.load();

      if (prefersReduced) {
        // Don't autoplay; keep poster frame.
        try { video.pause(); } catch (_) { /* noop */ }
        video.removeAttribute('autoplay');
        return;
      }

      // Be polite on mobile data: only autoplay once in viewport.
      if ('IntersectionObserver' in window) {
        let hasAttemptedPlay = false;
        const io = new IntersectionObserver((entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              if (!hasAttemptedPlay) {
                hasAttemptedPlay = true;
                const p = video.play();
                if (p && typeof p.catch === 'function') {
                  p.catch(() => { /* autoplay blocked — stays on poster, fine */ });
                }
              } else {
                video.play().catch(() => {});
              }
            } else {
              try { video.pause(); } catch (_) {}
            }
          }
        }, { threshold: 0.15 });
        io.observe(this);
      } else {
        // Fallback: try to autoplay immediately.
        const p = video.play && video.play();
        if (p && typeof p.catch === 'function') p.catch(() => {});
      }
    }
  }

  customElements.define('sk-video-background', SkVideoBackground);
})();
